import React, { useState } from 'react';
import { auth } from '../firebaseConfig';
import { signOut } from 'firebase/auth';
import { requestVerificationEmail } from '../services/emailActionsService';
import { friendlyAuthError } from '../utils/friendlyAuthError';
import './comic.css';

// Low-key text-link style, matching App.js's own linkStyle -- not exported
// from there, so duplicated here rather than threading a prop through for
// one shared constant (same call this app already made for ProfileGate/
// UsernameGate, which are similarly self-contained).
const linkStyle = {
  background: "none",
  border: "none",
  padding: 0,
  color: "#232526",
  fontFamily: "'Anonymous Pro', monospace",
  fontSize: "14px",
  textDecoration: "underline",
  cursor: "pointer"
};

// Blocking screen shown after signup until the account's email is confirmed
// real -- per direct user request ("I don't want people using fake email
// addresses... let them know if there is an issue so they can provide a
// valid address"). Firebase's own sendEmailVerification/emailVerified pair
// is the standard mechanism: a link is emailed to the address, and it can
// only be proven "real" by someone with actual inbox access clicking it.
// App.js scopes when this gate appears (see its own comment) -- this
// component only handles the UI once shown: check status, resend, or bail
// out and sign up again with a different (correctly-typed) address.
const EmailVerificationGate = ({ onVerified }) => {
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const handleCheck = async () => {
    if (checking) return;
    setChecking(true);
    setError(null);
    setNotice(null);
    try {
      // reload() mutates the same auth.currentUser object in place rather
      // than handing back a new reference, so checking .emailVerified right
      // after is safe here (we're not relying on a re-render to see it).
      await auth.currentUser.reload();
      if (auth.currentUser.emailVerified) {
        onVerified();
      } else {
        setError("Still not verified. Check your inbox (and spam folder) for the link, or resend it below.");
      }
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    setError(null);
    setNotice(null);
    try {
      const result = await requestVerificationEmail();
      if (!result.success) {
        setError(result.error);
        return;
      }
      setNotice("Verification email resent. Check your inbox.");
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="comic-panel comic-panel--outer-active" style={{ textAlign: "center" }}>
      <h2 className="comic-title" style={{ fontSize: "24px" }}>Verify Your Email</h2>
      <p className="comic-subtitle">
        We sent a confirmation link to <strong>{auth.currentUser?.email}</strong>. Click it, then come back here.
      </p>
      <button
        type="button"
        onClick={handleCheck}
        disabled={checking}
        className="comic-btn comic-btn--go"
      >
        {checking ? "Checking..." : "I've Verified My Email"}
      </button>
      <button
        type="button"
        onClick={handleResend}
        disabled={resending}
        className="comic-btn comic-btn--end"
      >
        {resending ? "Resending..." : "Resend Verification Email"}
      </button>
      {notice && <div className="comic-alert-bubble">{notice}</div>}
      {error && <div className="comic-alert-bubble">{error}</div>}
      <div style={{ marginTop: "16px" }}>
        <button type="button" onClick={() => signOut(auth)} style={linkStyle}>
          Typo in your email? Log out and sign up again
        </button>
      </div>
    </div>
  );
};

export default EmailVerificationGate;
